import {
  useGetDemoQuery,
  useInfiniteScroll,
  useLazyGetWebObjectQuery,
} from '@carboncopyco/data';
import type { IDemoPage } from '@carboncopyco/types';
import {
  Grid,
  SkeletonImageLoading,
  TextLargeParagraph,
} from '@carboncopyco/ui';
import { useEffect } from 'react';
import { InfiniteList } from '../../../InfiniteList';
import { TitleCard } from '../../InfoPageCards/TitleCard';
import { useDemoPageProps } from '../DemoPageContext';
import { DemoScreenTile } from './DemoScreenTile';

export const ScreensCard = () => {
  const { goToEditor, goToDemoPreview, organizationId, demoId, workspaceId } =
    useDemoPageProps();
  const { data: demo } = useGetDemoQuery({
    organizationId,
    workspaceId,
    id: demoId,
  });
  const {
    items: screens,
    isLoading: isLoadingDemo,
    error: errorDemo,
    isLoadingBatch,
    fetchFirstBatch,
    fetchNextBatch,
  } = useInfiniteScroll<IDemoPage>(useLazyGetWebObjectQuery);

  const fetchScreensArgs = {
    organizationId,
    sourceType: 'demo',
    sourceId: demoId,
    revisionId: String(demo?.revisionId),
  };

  useEffect(() => {
    if (demo) {
      fetchFirstBatch(fetchScreensArgs);
    }
  }, [demo]);

  return (
    <TitleCard
      header={
        <div className="flex items-center gap-2 px-6 h-ds-15">
          <TextLargeParagraph
            color="text-ds-natural-700"
            label="Screens"
          />
          {screens && <TextLargeParagraph label={`(${screens.length})`} />}
        </div>
      }
      radius
    >
      <div className="h-96 p-6">
        <div className="h-full p-6 overflow-hidden overflow-x-hidden">
          <InfiniteList
            onReachBottom={() => fetchNextBatch(fetchScreensArgs)}
            error={
              errorDemo && {
                message: 'Error fetching screens.',
              }
            }
            isLoading={isLoadingDemo}
            loader={
              <SkeletonImageLoading
                cols={4}
                rows={2}
              />
            }
            isLoadingBatch={isLoadingBatch}
          >
            <Grid<IDemoPage>
              gap="xl"
              keyName="url"
              data={screens}
              renderItem={(screen) => (
                <div className="pr-1">
                  <DemoScreenTile
                    firstUrl={demo?.firstUrl || ''}
                    screen={screen}
                    goToEditor={goToEditor}
                    goToDemoPreview={(url) =>
                      goToDemoPreview({ firstUrl: url })
                    }
                  />
                </div>
              )}
            />
          </InfiniteList>
        </div>
      </div>
    </TitleCard>
  );
};
